import React, { useState, useEffect } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { User, LogOut, Zap, Code, BookOpen, CircleAlert, CheckCircle2, X } from 'lucide-react';
import './Auth.css';

const AuthCloseButton = ({ onClose }) => {
  if (!onClose) return null;

  return (
    <button
      type="button"
      className="auth-modal-close"
      onClick={onClose}
      aria-label="Close sign in"
    >
      <X size={18} strokeWidth={2.25} />
    </button>
  );
};

const Auth = ({ onClose }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState(null);

  const signInErrorMessage = (error) => {
    const code = error?.code || '';
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      return 'The sign-in window was closed before finishing. Try again when you are ready.';
    }
    if (code === 'auth/popup-blocked') {
      return 'Your browser blocked the sign-in popup. Allow popups for this site and try again.';
    }
    if (code === 'auth/network-request-failed') {
      return 'Network error. Check your connection and try again.';
    }
    if (code === 'auth/unauthorized-domain') {
      return 'This domain is not authorized for sign-in. Contact support if this persists.';
    }
    return 'Something went wrong while signing in. Please try again.';
  };

  useEffect(() => {
    console.log('Auth component mounted, setting up auth state listener...');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('Auth state changed:', user);
      setUser(user);
      setLoading(false);
      // Close modal after successful sign in
      if (user && onClose) {
        setTimeout(() => onClose(), 500);
      }
    });

    return () => unsubscribe();
  }, [onClose]);

  const handleGoogleSignIn = async () => {
    try {
      setSigningIn(true);
      setSignInError(null);
      console.log('Attempting Google sign-in...');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Sign-in successful:', result.user);
    } catch (error) {
      console.error('Error signing in:', error);
      setSignInError(signInErrorMessage(error));
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="auth-card">
        <AuthCloseButton onClose={onClose} />
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return (
      <div className="auth-card">
        <AuthCloseButton onClose={onClose} />
          <div className="auth-header">
            <div className="user-avatar">
              {user.photoURL ? (
                <img src={user.photoURL} alt="Profile" />
              ) : (
                <User size={40} />
              )}
            </div>
            <h2>Welcome back!</h2>
            <p className="user-name">{user.displayName || user.email}</p>
            <p className="user-email">{user.email}</p>
          </div>

          <div className="auth-features">
            <h3>What you can do:</h3>
            <div className="feature-grid">
              <div className="feature-item">
                <Code size={24} />
                <span>Solve quantum problems</span>
              </div>
              <div className="feature-item">
                <BookOpen size={24} />
                <span>Access learning resources</span>
              </div>
              <div className="feature-item">
                <Zap size={24} />
                <span>Track your progress</span>
              </div>
            </div>
          </div>

          <button 
            className="auth-button sign-out-button"
            onClick={handleSignOut}
          >
            <LogOut size={20} />
            Sign Out
          </button>
        </div>
    );
  }

  return (
    <div className="auth-card auth-card--signin">
      <AuthCloseButton onClose={onClose} />

      <div className="auth-header auth-header--signin-welcome">
        <h1 className="auth-modal-brand-title">QubitCompile</h1>
        <p className="auth-subtitle">
          Sign in to unlock problems, resources, and the quantum simulator.
        </p>
      </div>

      <ul className="auth-benefits">
        <li>
          <CheckCircle2 size={17} />
          <span>Track progress across quantum problems</span>
        </li>
        <li>
          <CheckCircle2 size={17} />
          <span>Save solutions and code snippets</span>
        </li>
        <li>
          <CheckCircle2 size={17} />
          <span>Access learning resources and the simulator</span>
        </li>
      </ul>

        {signInError && (
          <div className="auth-signin-error" role="alert">
            <div className="auth-signin-error__icon" aria-hidden>
              <CircleAlert size={22} strokeWidth={2} />
            </div>
            <div className="auth-signin-error__body">
              <p className="auth-signin-error__title">Couldn&apos;t sign in</p>
              <p className="auth-signin-error__text">{signInError}</p>
            </div>
            <button
              type="button"
              className="auth-signin-error__dismiss"
              onClick={() => setSignInError(null)}
              aria-label="Dismiss error message"
            >
              ×
            </button>
          </div>
        )}

        <button 
          className="auth-button google-signin-button"
          onClick={handleGoogleSignIn}
          disabled={signingIn}
        >
          {signingIn ? (
            <>
              <div className="spinner small"></div>
              Signing in...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <p className="auth-privacy">
          By signing in, you agree to our Terms of Service and Privacy Policy.
          Your progress and solutions will be saved to your account.
        </p>
    </div>
  );
};

export default Auth;
